import React, { useState, useEffect } from "react";
import Container from "../common/Container";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    name: "Michael Johnson",
    company: "NovaStack",
    review:
      "Brightly’s team was easy to work with and very transparent throughout the project. They delivered what they promised and handled changes without any issues.",
  },
  {
    name: "Emily Carter",
    company: "Cloudnest Labs",
    review:
      "We needed a reliable development partner, and Brightly fit in well with our internal team. Communication was clear and deadlines were met.",
  },
  {
    name: "David Miller",
    company: "PixelForge",
    review:
      "The engineers clearly knew what they were doing. Code quality was solid and the system has been running smoothly since launch.",
  },
  {
    name: "Sarah Thompson",
    company: "DataCrest",
    review:
      "Brightly helped us refactor a complex backend. They explained trade-offs clearly and made good technical decisions along the way.",
  },
  {
    name: "James Anderson",
    company: "BrightFlow",
    review:
      "What stood out was their consistency. No surprises, no last-minute issues — just steady progress and clean delivery.",
  },
  {
    name: "Laura Bennett",
    company: "CodeHatch",
    review:
      "We appreciated how structured their process was. Documentation and handover were handled properly, which made things easier for us.",
  },
  {
    name: "Robert Williams",
    company: "VertexSoft",
    review:
      "Brightly worked like a long-term partner rather than a short-term vendor. They cared about building things the right way.",
  },
  {
    name: "Jessica Moore",
    company: "NeuralLoop",
    review:
      "The team was responsive and proactive. They flagged potential issues early and helped us avoid problems down the line.",
  },
  {
    name: "Daniel Harris",
    company: "Skybound Tech",
    review:
      "Our infrastructure setup is far more stable now. Brightly helped us simplify a lot of things that were overcomplicated before.",
  },
  {
    name: "Olivia Martinez",
    company: "InfraZen",
    review:
      "They were realistic with timelines and very honest about what could be achieved. That made planning much easier for us.",
  },
  {
    name: "Matthew Robinson",
    company: "Appmint",
    review:
      "Brightly took time to understand our product instead of rushing into development. The result was a much cleaner solution.",
  },
  {
    name: "Sophia Clark",
    company: "LogicTrail",
    review:
      "The collaboration felt smooth from day one. Design and development stayed well aligned throughout the project.",
  },
  {
    name: "Andrew Lewis",
    company: "Corelytics",
    review:
      "We had some complex backend requirements, and Brightly handled them efficiently without overengineering the solution.",
  },
  {
    name: "Ryan Walker",
    company: "Stackory",
    review:
      "Everything was delivered as expected. No drama, no delays — just solid engineering work.",
  },
  {
    name: "Nicole Adams",
    company: "DevSpring",
    review:
      "Brightly showed strong ownership and attention to detail. We felt comfortable trusting them with critical parts of our system.",
  },
];


const VISIBLE_CARDS_DESKTOP = 3;

const TestimonialsCarousel = () => {
  const [index, setIndex] = useState(0);

  const next = () => {
    setIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setIndex((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  // Auto-slide
  useEffect(() => {
    const timer = setInterval(next, 4500);
    return () => clearInterval(timer);
  }, []);

  // Get visible testimonials (wrap-around logic)
  const visibleTestimonials = Array.from(
    { length: VISIBLE_CARDS_DESKTOP },
    (_, i) => testimonials[(index + i) % testimonials.length]
  );

  return (
    <section className="bg-slate-50 py-32">
      <Container>
        <p className="text-center text-sm font-semibold uppercase tracking-wide text-slate-500">
          Client Testimonials
        </p>
        <h2 className="mt-3 text-center text-3xl sm:text-4xl font-bold text-slate-900">
          What teams across the US say about Brightly
        </h2>

        {/* Desktop carousel */}
        <div className="relative mt-20 hidden md:block">
          <div className="grid grid-cols-3 gap-8">
            {visibleTestimonials.map((item, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                {/* Stars */}
                <div className="flex gap-1 text-yellow-400">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={16} fill="currentColor" />
                  ))}
                </div>

                {/* Review */}
                <p className="mt-5 text-sm leading-relaxed text-slate-600">
                  “{item.review}”
                </p>

                {/* Author */}
                <div className="mt-6 border-t border-slate-100 pt-4">
                  <p className="font-semibold text-slate-900">{item.name}</p>
                  <p className="text-sm text-slate-500">
                    {item.company}, United States
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <button
            onClick={prev}
            className="absolute -left-14 top-1/2 -translate-y-1/2 rounded-full border border-slate-300 bg-white p-3 hover:bg-slate-100"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            onClick={next}
            className="absolute -right-14 top-1/2 -translate-y-1/2 rounded-full border border-slate-300 bg-white p-3 hover:bg-slate-100"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Mobile carousel (single card) */}
        <div className="mt-16 md:hidden">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex gap-1 text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} fill="currentColor" />
              ))}
            </div>

            <p className="mt-5 text-sm text-slate-600 leading-relaxed">
              “{testimonials[index].review}”
            </p>

            <div className="mt-6">
              <p className="font-semibold text-slate-900">
                {testimonials[index].name}
              </p>
              <p className="text-sm text-slate-500">
                {testimonials[index].company}, United States
              </p>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default TestimonialsCarousel;
